'use client';

import { AnimatePresence, motion } from 'motion/react';
import Script from 'next/script';
import { useEffect, useRef, useState } from 'react';
import { cx } from 'react-twc';
import { useBoolean, useOnClickOutside } from 'usehooks-ts';

import { useAiReplyGPT, useAiReplyV1, useAiReplyV2 } from '@/api-hooks/useAiReplies';
import ContainedButton from '@/components/Button/ContainedButton';
import OutlinedButton from '@/components/Button/OutlinedButton';
import Paper from '@/components/Paper';
import Stars from '@/components/Stars';
import TypingEffect from '@/components/TypingEffect';
import Image from '@/components/v2/Image';
import CheckLang from '@/features/CheckLang/CheckLang';
import ArrowRight from '@/icons/ArrowRight';
import StarOutline from '@/icons/StarOutline';
import { convertV1Reply } from '@/utils/convertV1Reply';

import Carousel from './_components/Carousel/Carousel';
import { sampleData } from './_const/sample';

const Playground = () => {
  const [rating, setRating] = useState(5);
  const [review, setReview] = useState('');
  const [shopName, setShopName] = useState('');
  const [userName, setUserName] = useState('');
  const [gptResponse, setGptResponse] = useState('');
  const [v1Response, setV1Response] = useState('');
  const [v2Response, setV2Response] = useState('');

  const { mutateAsync: gptMutationAsync, isPending: gptIsPending, isSuccess } = useAiReplyGPT();

  const { mutateAsync: v1MutationAsync, isPending: v1IsPending } = useAiReplyV1();

  const { mutateAsync: v2MutationAsync, isPending: v2IsPending } = useAiReplyV2();

  const wrapperRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { value: isWritable, setFalse: setUnwritable, setTrue: setWritable } = useBoolean(false);

  useOnClickOutside(wrapperRef as any, setUnwritable);

  useEffect(() => {
    if (isWritable) {
      textareaRef.current?.focus();
      setTimeout(() => {
        textareaRef.current?.setSelectionRange(review.length, review.length);
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWritable]);

  const handleClickStar = (e: React.MouseEvent<HTMLButtonElement>) => {
    const star = e.currentTarget.dataset.value;
    setRating(Number(star));
  };

  const handleClickRequest = async () => {
    setGptResponse('');
    setV1Response('');
    setV2Response('');
    setUnwritable();

    const body = {
      content: review,
      rating_recommend: rating,
      shops_name: shopName,
      reviewer: userName,
    };

    Promise.all([gptMutationAsync(body), v1MutationAsync(body), v2MutationAsync(body)]).then(
      (values) => {
        setGptResponse(values[0].content);
        setV1Response(
          convertV1Reply({
            value: values[1].content,
            userName,
            shopName,
          }),
        );
        setV2Response(values[2].content);
      },
    );
  };

  const loadingBarAnimation = {
    initial: { left: '-100%' },
    animate: {
      left: '0%',
      transition: {
        duration: 7,
        ease: 'easeInOut' as const,
        times: [0, 0.5, 0.8, 1],
        repeat: 0,

        delay: 0.5,
      },
    },
  };

  const isPending = gptIsPending || v1IsPending || v2IsPending;

  return (
    <>
      <div className="text-center py-12 md:py-10">
        <h2 className="text-balance break-keep font-bold">
          댓글몽 AI의 기특한 답변 생성 능력을 경험해 보세요
        </h2>
        <p className="text-gray-3 mt-4 text-md md:text-xl font-semibold md:mt-2">
          별점과 리뷰 내용에 따라 최적의 답변 초안을 만들어 드려요
        </p>
      </div>

      <Paper>
        <div ref={wrapperRef}>
          <div className="flex justify-between items-center">
            <div className="flex gap-3 items-center">
              <Image
                className="w-11 h-11 rounded-full border"
                src="/img/profile-male.png"
                alt="default_profile"
                width={44}
                height={44}
              />
              {isWritable ? (
                <>
                  <p className="font-semibold text-md md:text-xl">별점을 선택해 주세요</p>
                  <ul className="hidden gap-2 h-9 lg:flex items-center">
                    {Array.from({ length: 5 }, (_, i) => i + 1).map((i) => (
                      <li className="list-none h-full" key={i}>
                        {rating == 6 - i ? (
                          <ContainedButton
                            className="h-full px-4 gap-1.5 font-normal leading-5"
                            color="primary"
                          >
                            <StarOutline className="size-5 fill-white text-primary" />
                            {6 - i}점
                          </ContainedButton>
                        ) : (
                          <OutlinedButton
                            className={'h-full px-4 gap-1.5 font-normal leading-5'}
                            data-value={6 - i}
                            onClick={handleClickStar}
                          >
                            <StarOutline className="size-5" />
                            {6 - i}점
                          </OutlinedButton>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <>
                  <p className="font-bold text-md md:text-xl ">{userName}</p>
                  <div className="flex items-center gap-1.5">
                    <Stars
                      count={rating}
                      className={cx(rating >= 5 ? 'text-purple' : 'text-error', 'size-4 md:size-5')}
                    />
                    <span
                      className={cx(
                        'font-medium text-md md:text-lg',
                        rating >= 5 ? 'text-purple' : 'text-error',
                      )}
                    >
                      {rating.toFixed(1)}
                    </span>
                  </div>
                </>
              )}
            </div>
            <div>
              <p className="text-md text-gray-3 font-medium">
                <span className="text-gray-4 font-bold text-md">{review.length ?? 0} </span>/ 1000
              </p>
            </div>
          </div>

          {/* mobile  */}
          <div className="lg:hidden">
            <AnimatePresence>
              {isWritable && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.15 }}
                  className="mt-4"
                >
                  <ul className="flex gap-1.5 h-8 items-center flex-wrap">
                    {Array.from({ length: 5 }, (_, i) => i + 1).map((i) => (
                      <li className="list-none h-full" key={i}>
                        {rating == 6 - i ? (
                          <ContainedButton
                            className="py-1 px-1.5 gap-1 font-normal text-sm"
                            color="primary"
                          >
                            <StarOutline className="size-4 fill-white text-primary" />
                            {6 - i}점
                          </ContainedButton>
                        ) : (
                          <OutlinedButton
                            className={'py-1 px-1.5 gap-1 font-normal text-sm'}
                            data-value={6 - i}
                            onClick={handleClickStar}
                          >
                            <StarOutline className="size-4" />
                            {6 - i}점
                          </OutlinedButton>
                        )}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <div className="xl:pl-14 py-4">
            <Carousel
              slides={sampleData}
              setShopName={setShopName}
              setRating={setRating}
              setReview={setReview}
              setUserName={setUserName}
              setWritable={setWritable}
              setUnwritable={setUnwritable}
              review={review}
            />
          </div>
          <div className="xl:pl-14">
            <div>
              <ContainedButton
                className={cx(
                  'w-full h-12 shadow-lg relative overflow-hidden drop-shadow-lg',
                  isPending && 'cursor-progress',
                  '*:pointer-events-none',
                )}
                data-gtm-id="playground-generate-reply"
                color="secondary"
                onClick={handleClickRequest}
              >
                <AnimatePresence>
                  {isPending && (
                    <motion.div
                      variants={loadingBarAnimation}
                      className="absolute -left-full top-0 h-full w-full bg-primary pointer-events-none"
                      initial={{ left: '-100%' }}
                      animate={{
                        left: '0%',
                        transition: {
                          duration: 10,
                          ease: 'easeInOut' as const,
                          times: [0, 0.5, 0.8, 1],
                          repeat: Infinity,
                          delay: 0.5,
                        },
                      }}
                      exit={{
                        left: '0%',
                        opacity: 0,
                        transition: {
                          duration: 0,
                          delay: 0.5,
                        },
                      }}
                    />
                  )}
                </AnimatePresence>

                <span className="z-30 flex gap-1 items-center pointer-events-none">
                  {isPending
                    ? '리뷰 내용을 반영한 최적의 답변 생성중...'
                    : '5초 만에 AI 답변 초안 생성해보기'}
                  {!isPending && <ArrowRight className="size-4" />}
                </span>
              </ContainedButton>
            </div>
            <div className="flex gap-2.5 h-7 items-center my-7">
              <Image src={'/img/icons/mong_eye.svg'} width={28} height={28} alt="mong_eye" />
              <h6>
                {isPending
                  ? '몽이가 답변을 생성하고 있어요...'
                  : '리뷰 답변 생성에 최적화된 댓글몽의 능력!'}
              </h6>
            </div>

            <div className="bg-gray-7 rounded-xl md:py-4 py-2 px-3 md:px-6">
              <div className="grid md:grid-cols-3 gap-5 mt-4 mb-1">
                <div>
                  <p className="text-center mb-3 text-lg md:text-xl text-gray-3 font-bold">
                    ChatGPT 생성 결과
                  </p>
                  <div className="py-4 px-4.5 rounded-xl bg-white min-h-[180px]">
                    {!!gptResponse ? (
                      <TypingEffect text={gptResponse} speed={20} />
                    ) : (
                      <p className="text-gray-4 text-lg" />
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-center mb-3 text-lg md:text-xl text-gray-3 font-bold">
                    댓글몽 ver 1.0
                  </p>

                  <div className="py-4 px-4.5 rounded-xl bg-white min-h-[180px] flex">
                    {!!v1Response ? (
                      <TypingEffect text={v1Response} speed={20} />
                    ) : (
                      <p className="text-gray-4 text-lg m-auto">
                        {isPending
                          ? 'AI 답변을 생성중입니다...'
                          : isSuccess
                            ? 'AI 답변을 생성중입니다...'
                            : 'AI 답변 초안 생성해보기 버튼을 클릭해 답변을 확인해보세요'}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-center mb-3 text-lg md:text-xl text-gray-3 font-bold">
                    댓글몽 ver 2.0
                  </p>
                  <div className="py-4 px-4.5 rounded-xl bg-white min-h-[180px]">
                    {!!v2Response ? (
                      <TypingEffect text={v2Response} speed={20} />
                    ) : (
                      <p className="text-gray-4 text-lg" />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <CheckLang />
      </Paper>
      <Script strategy="lazyOnload" id="wcs-">
        {`var _conv = {}; 
_conv.type="custom019"; 
_conv.id="top_experience_click_56789";
if(wcs){
  wcs.trans(_conv); 
}`}
      </Script>
    </>
  );
};

export default Playground;
